import { useState } from 'react';
import Button from '../Button';
import { submitContactMessage } from '../../services/api';

const INITIAL = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  message: '',
};

function validateEmail(val) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
}

function validate(fields) {
  const errors = {};
  if (!fields.first_name.trim()) errors.first_name = 'First name is required.';
  if (!fields.last_name.trim()) errors.last_name = 'Last name is required.';
  if (!fields.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!validateEmail(fields.email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!fields.message.trim()) errors.message = 'Message is required.';
  return errors;
}

/**
 * ContactForm
 *
 * Controlled form that posts to POST /contact-message.
 * Props:
 *   onSuccess — optional callback after successful submission
 */
function ContactForm({ onSuccess }) {
  const [fields, setFields] = useState(INITIAL);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  function handleBlur(e) {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldErrors = validate(fields);
    if (fieldErrors[name]) {
      setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched({ first_name: true, last_name: true, email: true, message: true });
    const fieldErrors = validate(fields);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setLoading(true);
    setSubmitError('');

    // Build body — omit empty optional fields
    const body = {
      first_name: fields.first_name.trim(),
      last_name: fields.last_name.trim(),
      email: fields.email.trim(),
      message: fields.message.trim(),
    };
    if (fields.phone.trim()) body.phone = fields.phone.trim();

    try {
      await submitContactMessage(body);
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 429) {
        setSubmitError("You're submitting too fast — please wait a minute and try again.");
      } else if (status === 400 && data?.message) {
        setSubmitError(data.message);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="form-success">
        <span className="form-success-icon" role="img" aria-label="Success">&#10003;</span>
        <h3>Message Sent!</h3>
        <p>Thanks for reaching out — we've received your message and will get back to you shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {submitError && (
        <div className="form-error-banner" role="alert">
          {submitError}
        </div>
      )}

      {/* Row 1: First + Last name */}
      <div className="form-grid">
        <div className="form-row">
          <label className="field-label" htmlFor="cm-first">
            First Name <span className="req">*</span>
          </label>
          <input
            id="cm-first"
            name="first_name"
            type="text"
            className={`field-input${errors.first_name ? ' field-invalid' : ''}`}
            value={fields.first_name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Jane"
            autoComplete="given-name"
          />
          {errors.first_name && (
            <span className="field-error" role="alert">{errors.first_name}</span>
          )}
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="cm-last">
            Last Name <span className="req">*</span>
          </label>
          <input
            id="cm-last"
            name="last_name"
            type="text"
            className={`field-input${errors.last_name ? ' field-invalid' : ''}`}
            value={fields.last_name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Smith"
            autoComplete="family-name"
          />
          {errors.last_name && (
            <span className="field-error" role="alert">{errors.last_name}</span>
          )}
        </div>
      </div>

      {/* Row 2: Email + Phone */}
      <div className="form-grid">
        <div className="form-row">
          <label className="field-label" htmlFor="cm-email">
            Email <span className="req">*</span>
          </label>
          <input
            id="cm-email"
            name="email"
            type="email"
            className={`field-input${errors.email ? ' field-invalid' : ''}`}
            value={fields.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="jane@example.com"
            autoComplete="email"
          />
          {errors.email && (
            <span className="field-error" role="alert">{errors.email}</span>
          )}
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="cm-phone">
            Phone
          </label>
          <input
            id="cm-phone"
            name="phone"
            type="tel"
            className="field-input"
            value={fields.phone}
            onChange={handleChange}
            placeholder="(555) 000-0000"
            autoComplete="tel"
          />
        </div>
      </div>

      {/* Row 3: Message */}
      <div className="form-row">
        <label className="field-label" htmlFor="cm-message">
          Message <span className="req">*</span>
        </label>
        <textarea
          id="cm-message"
          name="message"
          className={`field-input${errors.message ? ' field-invalid' : ''}`}
          value={fields.message}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="How can we help you?"
          rows={5}
        />
        {errors.message && (
          <span className="field-error" role="alert">{errors.message}</span>
        )}
      </div>

      <div className="form-submit-row">
        <Button
          variant="primary"
          type="submit"
          disabled={loading}
          className={loading ? 'btn-submitting' : ''}
        >
          {loading ? 'Sending…' : 'Send Message'}
        </Button>
      </div>
    </form>
  );
}

export default ContactForm;
